import type { AxiosError, AxiosResponse } from 'axios';

export type ApiErrorCode =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'CANCELED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'SERVER'
  | 'UNKNOWN';

interface NestValidationPayload {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  /**
   * The array ox-platform's global filter adds (`http-exception.filter.ts`):
   * the `ValidationPipe` 400 arrives as `message: 'Bad Request Exception'`
   * plus the real class-validator lines here.
   */
  errors?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly payload: unknown;
  readonly fields?: Record<string, string>;
  /** Milliseconds the server asked us to wait (Retry-After), when readable. */
  readonly retryAfterMs?: number;

  constructor(opts: {
    message: string;
    status: number;
    code: ApiErrorCode;
    payload?: unknown;
    fields?: Record<string, string>;
    retryAfterMs?: number;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.payload = opts.payload;
    this.fields = opts.fields;
    this.retryAfterMs = opts.retryAfterMs;
  }

  static fromAxios(err: AxiosError): ApiError {
    if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
      return new ApiError({
        message: 'Request canceled',
        status: 0,
        code: 'CANCELED',
        payload: err.message,
      });
    }
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError({
        message: 'Request timed out',
        status: 0,
        code: 'TIMEOUT',
        payload: err.message,
      });
    }
    if (!err.response) {
      return new ApiError({
        message: err.message || 'Network error',
        status: 0,
        code: 'NETWORK',
        payload: err.message,
      });
    }
    return ApiError.fromResponse(err.response);
  }

  /**
   * Two body shapes reach us:
   *   · raw Nest — `message: string | string[]`;
   *   · ox-platform's global filter — `message` is `exception.message` and the
   *     detail goes to `errors: string[]`. For a validation 400 that `message`
   *     is the fixed "Bad Request Exception" and the field lines live only in
   *     `errors`.
   * `fields` reads `errors` when it carries strings, so a form can name the
   * field. `message` is deliberately left as it was: every toast in the app
   * shows it, and rewriting it would change what all of them say.
   */
  static fromResponse(res: AxiosResponse): ApiError {
    const status = res.status;
    const payload = res.data as NestValidationPayload | undefined;
    const rawMessage = payload?.message;
    const messages = Array.isArray(rawMessage) ? rawMessage : rawMessage ? [rawMessage] : [];
    const message = messages[0] ?? payload?.error ?? `Request failed (${status})`;
    const detalle = Array.isArray(payload?.errors)
      ? payload.errors.filter((m): m is string => typeof m === 'string')
      : [];

    return new ApiError({
      message,
      status,
      code: codeFromStatus(status),
      payload,
      fields: extractFieldErrors(detalle.length > 0 ? detalle : messages),
      retryAfterMs: retryAfterMsFrom(res.headers),
    });
  }
}

/**
 * Reads the Retry-After header (seconds or HTTP date) as milliseconds.
 * Never throws: anything missing, empty, negative or unparsable gives undefined.
 * Note: it only arrives if ox-platform's CORS config lists Retry-After in its
 * exposed headers; otherwise, in a browser with app and API on different
 * origins, this returns undefined.
 */
function retryAfterMsFrom(headers: unknown, now: number = Date.now()): number | undefined {
  try {
    if (!headers || typeof headers !== 'object') return undefined;
    const h = headers as { get?: (name: string) => unknown } & Record<string, unknown>;
    const raw = typeof h.get === 'function' ? h.get('retry-after') : h['retry-after'];
    if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
    const value = String(raw).trim();
    if (/^\d+$/.test(value)) return Number(value) * 1000;
    // An HTTP date always has a month name; Date.parse would read "-3" as a year.
    if (!/[a-z]{3}/i.test(value)) return undefined;
    const date = Date.parse(value);
    if (Number.isNaN(date)) return undefined;
    return Math.max(0, date - now);
  } catch {
    return undefined;
  }
}

function codeFromStatus(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 422 || status === 400) return 'VALIDATION';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER';
  return 'UNKNOWN';
}

/**
 * NestJS class-validator returns messages like "email must be an email".
 * Map back to { field: message } when the first token matches a property.
 * Fed with the global filter's `errors[]` when present (see `fromResponse`);
 * otherwise with `message`, as before.
 */
function extractFieldErrors(messages: string[]): Record<string, string> | undefined {
  if (messages.length === 0) return undefined;
  const fields: Record<string, string> = {};
  for (const msg of messages) {
    const match = /^([a-zA-Z][a-zA-Z0-9_]*)\s+(.*)$/.exec(msg);
    if (match) {
      const [, field, rest] = match;
      if (!fields[field]) fields[field] = rest;
    }
  }
  return Object.keys(fields).length > 0 ? fields : undefined;
}
